import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import type { SignatureValueDto } from '@lib/dto/envelope.dto'
import { cn } from '@/lib/utils'
import { usePaper } from '@/theme/ThemeProvider'

/** Movement, in canvas pixels, before a pointer counts as ink rather than a click. */
const MIN_STROKE_PX = 3

/** True when the pad has a pixel that is not fully transparent. */
function canvasHasInk(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext('2d')
  if (!context || canvas.width === 0 || canvas.height === 0) {
    return false
  }
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height)
  for (let index = 3; index < data.length; index += 4) {
    if ((data[index] ?? 0) > 0) {
      return true
    }
  }
  return false
}

export function SignaturePad({
  label,
  value,
  onChange,
}: {
  label: string
  value: SignatureValueDto | undefined
  onChange: (value: SignatureValueDto) => void
}) {
  const canvasId = React.useId()
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const drawingRef = React.useRef(false)
  const inkRef = React.useRef(false)
  const startRef = React.useRef({ x: 0, y: 0 })
  const typed = value?.kind === 'type' ? value.data : ''
  // The pad is paper on a dark page too: the ink is stamped on a white document.
  const paper = usePaper()

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }
    const color = getComputedStyle(canvas).color
    if (color) {
      ctx.strokeStyle = color
    }
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
  }, [paper])

  function point(event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current
    if (!canvas) {
      return { x: 0, y: 0 }
    }
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  function applyDrawing(): void {
    const canvas = canvasRef.current
    // An untouched canvas still encodes as a PNG. That image is non-empty, so
    // it would count as a signature and replace whatever was typed.
    if (!canvas || !canvasHasInk(canvas)) {
      return
    }
    onChange({ kind: 'draw', data: canvas.toDataURL('image/png') })
  }

  function endStroke(): void {
    const drew = inkRef.current
    drawingRef.current = false
    inkRef.current = false
    if (drew) {
      applyDrawing()
    }
  }

  function clear(): void {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    onChange({ kind: 'type', data: '' })
  }

  return (
    <Field label={label} htmlFor={canvasId}>
      <canvas
        id={canvasId}
        aria-label={label}
        ref={canvasRef}
        width={320}
        height={96}
        className={cn('h-24 w-full touch-none rounded-md border border-input bg-background', paper)}
        onPointerDown={(event) => {
          drawingRef.current = true
          inkRef.current = false
          event.currentTarget.setPointerCapture(event.pointerId)
          const ctx = canvasRef.current?.getContext('2d')
          const next = point(event)
          startRef.current = next
          ctx?.beginPath()
          ctx?.moveTo(next.x, next.y)
        }}
        onPointerMove={(event) => {
          if (!drawingRef.current) {
            return
          }
          const next = point(event)
          // A click jitters a pixel or two. Ink starts only after a real stroke,
          // so that click cannot become a blank or one-dot signature.
          if (!inkRef.current) {
            const dx = next.x - startRef.current.x
            const dy = next.y - startRef.current.y
            if (dx * dx + dy * dy < MIN_STROKE_PX * MIN_STROKE_PX) {
              return
            }
            inkRef.current = true
          }
          const ctx = canvasRef.current?.getContext('2d')
          ctx?.lineTo(next.x, next.y)
          ctx?.stroke()
        }}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      />
      <Input
        value={typed}
        // The Field's label names the drawing pad; this box needs its own.
        aria-label={`Type your ${label.toLowerCase()}`}
        placeholder="Or type your name"
        onChange={(event) => {
          const canvas = canvasRef.current
          const ctx = canvas?.getContext('2d')
          if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
          }
          onChange({ kind: 'type', data: event.target.value })
        }}
      />
      <Button type="button" variant="ghost" size="sm" className="self-start" onClick={clear}>
        Clear
      </Button>
    </Field>
  )
}
